import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { useConnections } from '@features/connections/generic/hooks/useConnections';
import { useActiveImportJobs } from '@features/connections/hooks/useActiveImportJobs';
import { useAggregatedForecast } from '@features/timetravel/hooks/useForecast';
import SalesConnectStoreOverlay from './SalesConnectStoreOverlay';
import SalesImportOverlay from './SalesImportOverlay';
import SalesForecastOverlay from './SalesForecastOverlay';

type SalesOverlayPhase = 'connect' | 'import' | 'forecast' | null;

/** True for order/sales sync targets (e.g. orders, orders-new, orders-all). */
export const isSalesImportTarget = (target: string) =>
  target === 'sales' || target === 'orders' || target.startsWith('orders');

const FORECAST_POLL_MS = 5000;

interface SalesOnboardingOverlayProps {
  /** Usually the sales chart — stays visible under the backdrop. */
  children: React.ReactNode;
}

/**
 * Overlays connect / import / forecast panels on top of the sales graph with a
 * backdrop. App navigation stays usable; only the chart area is covered.
 */
const SalesOnboardingOverlay: React.FC<SalesOnboardingOverlayProps> = ({ children }) => {
  const { connections } = useConnections();
  const { jobs: activeJobs, isFetched: jobsFetched } = useActiveImportJobs();
  const { aggregatedForecast, isFetched: forecastFetched } = useAggregatedForecast({
    pollUntilReadyMs: FORECAST_POLL_MS,
  });

  const phase = useMemo<SalesOverlayPhase>(() => {
    if (connections === undefined) return null;

    if (connections.length === 0) return 'connect';

    if (!jobsFetched || !forecastFetched) return null;

    const isImportingSales = activeJobs.some((job) => isSalesImportTarget(job.target));
    if (isImportingSales) return 'import';

    const hasForecast = (aggregatedForecast?.length ?? 0) > 0;
    if (!hasForecast) return 'forecast';

    return null;
  }, [activeJobs, aggregatedForecast, connections, forecastFetched, jobsFetched]);

  return (
    <Box sx={{ position: 'relative', minHeight: '550px' }}>
      {children}

      {phase && (
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(40px)',

          }}
        >
          {phase === 'connect' && <SalesConnectStoreOverlay />}
          {phase === 'import' && <SalesImportOverlay />}
          {phase === 'forecast' && <SalesForecastOverlay />}
        </Box>
      )}
    </Box>
  );
};

export default SalesOnboardingOverlay;
